// ==============================|| PRICELIST TYPES ||============================== //

import { JobRoleDto } from './jobRoles';
import { NamedEntityMinDto } from './other';

export enum PricelistStateType {
    VALID = 'VALID',
    EXPIRED = 'EXPIRED',
    SCHEDULED = 'SCHEDULED',
    MODEL = 'MODEL',
    MATCHES_MODEL = 'MATCHES_MODEL',
    CASE_BASED = 'CASE_BASED',
    CONTRACT_BASED = 'CONTRACT_BASED',
}

// How a list prices work: by job role, or per person in dated periods. Absent on a read means ROLE.
export enum PricingKind {
    ROLE = 'ROLE',
    EMPLOYEE = 'EMPLOYEE',
}

export interface Fetchable {
    id: number;
    name: string;
    matterId?: number;
    scopeTypeCode: string;
    pricingKind?: PricingKind;
    currencyTypeCode?: string;
    minHourlyPrice?: string;
    maxHourlyPrice?: string;
    avgHourlyPrice?: string;
    labelsList?: string[];
}

export interface CalculatedPricelist extends Fetchable {
    // The case-based list's own stored custom name (undefined when none); pre-fills the rename field.
    customName?: string;
    validFrom?: Date;
    validTo?: Date;
    clientId?: number;
    clientName?: string;
    clientFirstName?: string;
    clientLastName?: string;
    companyId?: number;
    companyName?: string;
    contractId?: number;
    contractName?: string;
    pricingHash: string;
    matters?: ConnectedMatterDto[];
    rates?: HourlyRate[];
    matterId?: number;
    pricelistId?: number;
    // The case list this catalogue row stands for; a case with a scheduled list shows one row per list.
    matterPricelistId?: number;
}

export interface ConnectedMatterDto {
    matterId: string;
    matterNumber: string;
    matterName: string;
    isConfidential?: boolean | null;
}

export interface Pricelist extends Fetchable {
    company?: {
        id: number;
        name: string;
    };
    contract?: {
        id: number;
        contractName: string;
        parties: {
            id: number;
            name: string;
        }[];
    };
    validFrom: Date;
    validTo?: Date;
    pricingHash?: string;
    rates?: HourlyRate[];
    // Set on an employee-priced list only, sorted by date with the undated first period first.
    periods?: PricelistPeriod[] | null;
    // The model a contract's employee-priced list follows; its periods are then the model's, read-only.
    followsPricelist?: { id: number; name: string } | null;
}

// One person's price inside a period. The job role and the activity are the person's today, for grouping the
// grid and marking who has left.
export interface EmployeeRate {
    employee: { id: number; name: string };
    jobRoleId: number | null;
    jobRoleName: string | null;
    employeeActive: boolean;
    employeeLeftOn: string | null;
    employeeDeletedAt: string | null;
    hourlyPrice: string | null;
}

// A dated set of prices of an employee-priced list: it applies from its date until the next period's date, and
// the first one has no date and covers everything before.
export interface PricelistPeriod {
    id: number;
    validFrom: string | null;
    inForce: boolean;
    rates: EmployeeRate[];
}

// A period a form holds until it saves: a stored one keeps its id, one added in the form has none.
export interface StagedPricelistPeriod {
    id: number | null;
    validFrom: string | null;
    rates: EmployeeRate[];
}

export interface EmployeeRateInput {
    employee: { id: number };
    hourlyPrice: string | null;
}

// One period of the full set a save carries. A null id is a new period; null rates copy the period before it on
// a new one and leave a stored one's prices untouched.
export interface PricelistPeriodWrite {
    id: number | null;
    validFrom: string | null;
    rates: EmployeeRateInput[] | null;
}

export interface PeriodImpact {
    contracts: number;
    cases: number;
    uninvoicedHours: number;
}

/** The periods a save re-prices work already done from: dated starts, and the undated first period. */
export interface RepricedStarts {
    validFrom: string[];
    fromStart: boolean;
}

export interface PricelistDto {
    id: number;
    name: string;
    scopeTypeCode: string;
    companyId: number | null;
    companyName: string;
    contractId: number | null;
    currencyTypeCode: string | null;
    currencyTypeName: string;
    validFrom: Date | null;
    validTo: Date | null;
    rates: HourlyRate[];
    pricingKind?: PricingKind;
    periods?: PricelistPeriodWrite[] | null;
    // Omitted keeps the link, null makes an own copy, an id follows that model.
    followsPricelist?: { id: number } | null;
}

export interface HourlyRate {
    jobRole: JobRoleDto;
    hourlyPrice: string | null;
}

// One row of a list's rates read: a job role's price, or on an employee-priced list a priced person's price in the
// period in force today, which carries no job role.
export interface PricelistRateRow {
    jobRole: JobRoleDto | null;
    hourlyPrice: string | null;
    employee?: { id: number; name: string };
}

// A price for a named person inside one of a case's lists; jobRole is the role the person holds today.
export interface PersonalRate {
    employee: NamedEntityMinDto;
    jobRole: JobRoleDto | null;
    hourlyPrice: string | null;
}

// One price list of a case as GET /v1/matters/{id}/rates returns it; validFrom is null on the base
// list, which applies from the case's opening date.
export interface MatterPricelist {
    id: number;
    // The same on every list of the case; absent on a read means ROLE.
    pricingKind?: PricingKind;
    // The same on every list of the case: the lists are then a read-only copy of the followed list's periods.
    followsPricelist?: MatterFollowedPricelist | null;
    validFrom: string | null;
    name: string | null;
    rates: HourlyRate[];
    personalRates: PersonalRate[];
    pricingHash: string;
    minHourlyPrice: string;
    maxHourlyPrice: string;
    avgHourlyPrice: string;
}

// The list a case follows. A contract list that follows a model list in turn names it, as the model's periods are
// the ones the case's lists copy.
export interface MatterFollowedPricelist {
    id: number;
    name: string;
    scopeTypeCode: ScopeType | string;
    contractId: number | null;
    modelPricelist: { id: number; name: string } | null;
}

// The case's billable hours a price change would re-price: on no invoice, and on an invoice not yet past review.
export interface MatterUninvoicedHours {
    hoursOnNoInvoice: number;
    hoursOnOpenInvoices: number;
}

export interface PersonalRateInput {
    employee: { id: number };
    hourlyPrice: string | null;
}

// One list of the full set a case save carries: the list's complete state. The id is the stored list's on an
// update; null on a list the form added, and on every list of a create or copy.
export interface MatterPricelistWriteDto {
    id: number | null;
    validFrom: string | null;
    name: string | null;
    rates: HourlyRate[];
    personalRates: PersonalRateInput[];
}

// One list as the case card and its popover show it: a stored list, or a tab the form stages with figures
// computed in the browser. The key stands in for the id, which a staged list may not have.
export interface PricelistCardEntry extends RateFigures {
    key: string;
    validFrom: string | null;
    name: string | null;
    rates: HourlyRate[];
    personalRates: PersonalRate[];
}

// The stored figures of a list, or the browser's computation of them for a staged list that has none yet.
export interface RateFigures {
    minHourlyPrice: string | number;
    maxHourlyPrice: string | number;
    avgHourlyPrice: string | number;
}

export interface CreateMatterPricelistDto {
    sourcePricelistId: number | null;
    validFrom: string;
    name: string | null;
    rates?: HourlyRate[] | null;
    personalRates?: PersonalRateInput[] | null;
}

export interface UpdateMatterPricelistHeaderDto {
    validFrom: string | null;
    name: string | null;
}

export enum ScopeType {
    COMPANY = 'COMPANY',
    CONTRACT = 'CONTRACT',
    TENANT = 'TENANT',
    MATTER = 'MATTER',
}

export enum PricelistLabelType {
    VALID = 'VALID',
    EXPIRED = 'EXPIRED',
    SCHEDULED = 'SCHEDULED',
    MODEL = 'MODEL',
    MATCHES_MODEL = 'MATCHES_MODEL',
    CASE_BASED = 'CASE_BASED',
    CONTRACT_BASED = 'CONTRACT_BASED',
}
